import React, { useState, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ModalTransition } from '@atlaskit/modal-dialog';

import { RecommendStandardOverPremiumModal } from '@atlassian/recommend-standard-over-premium';

import { Attribution, withTransparentErrorBoundary } from '@confluence/error-boundary';
import { ScreenEvent } from '@confluence/analytics';
import {
	RECOMMEND_BUY_STANDARD_PREMIUM_TRIAL,
	ExperienceStart,
	ExperienceSuccess,
} from '@confluence/experience-tracker';

import { usePremiumDirectBuyStandardExperiment } from '../hooks/usePremiumDirectBuyStandardExperiment';
import { useFireExposureEvents } from '../hooks/useFireExposureEvents';
import { usePricing } from '../hooks/usePricing';

export function RecommendStandardOverPremiumModalContainer() {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [isOpen, setOpen] = useState(true);
	const [canFireExposureEvents, setCanFireExposureEvents] = useState(true);
	const {
		loading,
		isEligible,
		isEligibleTreatment,
		trialEndDate,
		changeOfferingAndPayLink,
		stopModalCoordination,
		analyticsAttributes,
	} = usePremiumDirectBuyStandardExperiment();

	const { fireExposureEvents } = useFireExposureEvents({
		skip: !isEligible,
		experimentName: 'confluence_eot_premium_recommend_standard_buy',
		extraAnalytics: analyticsAttributes,
	});

	const { standardPrice, premiumPrice } = usePricing();

	useEffect(() => {
		if (isEligible && canFireExposureEvents) {
			fireExposureEvents();
			setCanFireExposureEvents(false);
		}
	}, [isEligible, fireExposureEvents, canFireExposureEvents, setCanFireExposureEvents]);

	const trialEndDateFormatted = new Date(`${trialEndDate}T00:00:00Z`);
	const onCloseModal = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'RecommendStandardOverPremiumModal',
				action: 'closed',
				actionSubject: 'modal',
				attributes: analyticsAttributes,
			},
		}).fire();
		setOpen(false);
		void stopModalCoordination();
	};

	const onBuyStandard = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'RecommendStandardOverPremiumModal',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'buyStandard',
				attributes: analyticsAttributes,
			},
		}).fire();
		window.open(changeOfferingAndPayLink, '_blank');
	};

	if (loading || !isEligibleTreatment) {
		return null;
	}

	return (
		<ModalTransition>
			{isOpen && isEligibleTreatment && (
				<>
					<ExperienceStart name={RECOMMEND_BUY_STANDARD_PREMIUM_TRIAL} />
					<ScreenEvent
						name="RecommendStandardOverPremiumModal"
						id="RecommendStandardOverPremiumModal"
						attributes={analyticsAttributes}
					/>
					<RecommendStandardOverPremiumModal
						productKey="Confluence"
						trialEndDate={trialEndDateFormatted}
						standardPrice={standardPrice}
						premiumPrice={premiumPrice}
						onClose={onCloseModal}
						onClickBuyStandard={onBuyStandard}
						analyticsAttributes={analyticsAttributes}
					/>
					<ExperienceSuccess name={RECOMMEND_BUY_STANDARD_PREMIUM_TRIAL} />
				</>
			)}
		</ModalTransition>
	);
}

export const RecommendStandardOverPremiumModalContainerWithErrorBoundary =
	withTransparentErrorBoundary({
		attribution: Attribution.GROWTH_LUNA,
	})(RecommendStandardOverPremiumModalContainer);
